"use client";

import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const beforeAfterComparison = [{
  number: "BEFORE",
  title: "Corroded & Aged System",
  subtitle: "Original Commercial Chilled Water Pump Assembly",
  category: "Before Restoration",
  image: "https://aaeagleplumbing.com/wp-content/uploads/2026/08/Chill-Water-Pumps2.jpeg",
  alt: "Corroded chilled water pumps before overhaul and restoration"
}, {
  number: "AFTER",
  title: "Rebuilt & Precision Aligned",
  subtitle: "Fully Restored High-Efficiency Pump System",
  category: "After Overhaul",
  image: "https://aaeagleplumbing.com/wp-content/uploads/2026/08/Chill-Water-Pumps1.jpeg",
  alt: "Refurbished and newly painted chilled water pump system after complete restoration"
}];
export default function ChillWaterPumpTransformation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-nav="light" id="completedservicesgallery" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Header Section */}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-12 md:mb-16 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7 flex gap-6 md:gap-10">
            <div className="hidden md:flex items-start pt-2">
              <span className="font-mono text-xs tracking-[0.25em] text-muted-foreground uppercase whitespace-nowrap" style={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)"
            }}>
                02 — Case Study
              </span>
            </div>
            <div className="flex-1">
              <span className="md:hidden font-mono text-xs tracking-[0.25em] text-muted-foreground uppercase mb-4 block">
                02 — Case Study
              </span>
              <h2 className="font-serif italic font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-foreground">
                Chilled water
                <br />
                transformation
              </h2>
            </div>
          </div>

          <div className="lg:col-span-5 lg:pt-6 flex flex-col justify-between gap-8">
            <p className="font-sans text-base md:text-lg leading-relaxed text-muted-foreground max-w-md">
              Witness the complete overhaul of a commercial chilled water pump system—from heavily corroded, inefficient components to a fully refurbished, precision-aligned, high-efficiency setup.
            </p>
            <Link to="/ServicesPage" className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] uppercase text-foreground hover:text-primary transition-colors duration-500 w-fit">
              <span className="border-b border-foreground/30 group-hover:border-primary pb-1 transition-colors duration-500">
                View all services
              </span>
              <ArrowUpRight className="size-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.25} />
            </Link>
          </div>
        </div>

        {/* Comparison Grid (2 Columns) */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {beforeAfterComparison.map((item, index) => <ComparisonCard key={index} item={item} className={`transition-all duration-1000 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
          transitionDelay: `${index * 150}ms`
        }} aspectClass="aspect-[4/3]" />)}
        </div>
      </div>
    </section>;
}
type ComparisonItem = {
  number: string;
  title: string;
  subtitle: string;
  category: string;
  image: string;
  alt: string;
};
function ComparisonCard({
  item,
  className = "",
  style,
  aspectClass
}: {
  item: ComparisonItem;
  className?: string;
  style?: React.CSSProperties;
  aspectClass: string;
}) {
  return <div className={className} style={style}>
      <div className="group block relative w-full">
        {/* Image Container */}
        <div className={`relative overflow-hidden ${aspectClass} bg-muted`}>
          <img src={item.image} alt={item.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
          {/* Subtle Category Badge */}
          <div className="absolute top-4 left-4 z-10">
            <span className="inline-block px-3 py-1 text-[11px] font-mono uppercase tracking-widest bg-background/90 text-foreground backdrop-blur-md">
              {item.category}
            </span>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif italic font-light text-2xl md:text-3xl text-foreground leading-tight">
              {item.title}
            </h3>
            <p className="font-sans text-sm text-muted-foreground mt-1">
              {item.subtitle}
            </p>
          </div>
          <span className="font-mono text-xs tracking-[0.2em] font-semibold text-muted-foreground whitespace-nowrap pt-1">
            {item.number}
          </span>
        </div>
      </div>
    </div>;
}